import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Chip, Divider, Grid, IconButton, Input, InputAdornment, Stack, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { FileDownloadOutlined } from "@mui/icons-material";
import moment from "moment";
import FilterBox from "../../../UiComponents/FilterBox/FilterBox";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import { getBriskV2Config, getBriskV2WalletLedger, getBriskV2WalletSummary } from "../../../Service/BriskV2Service";
import BriskLedgerTable from "./BriskLedgerTable";
import { MismatchAlert, MoneyCard, agencyOptions } from "./BriskWalletParts";
import { LEDGER_EXCEL_HEADERS, LEDGER_TYPE_FILTERS, dayParam, ledgerExcelRows, saveExcel } from "./briskWalletUtils";

/**
 * Transaction Report (owner, 2026-10-30, after Girnar's page) — every movement of the agency's Company Wallet "Brisk":
 * top-ups, Brisk online top-ups, certificates and their refunds, Online Policy PA charges and refunds, removals. Each row
 * shows the balance the wallet stored right after it. Admin only; the super admin sees every agency (Agency filter).
 */
const BriskTransactionReport = () => {
  const [config, setConfig] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState("");
  const [agency, setAgency] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [applied, setApplied] = useState({ type: "", clientId: "", startDate: null, endDate: null });
  const [showFilter, setShowFilter] = useState(false);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const superAdmin = !!(config && config.isSuperAdmin);

  useEffect(() => {
    getBriskV2Config().then((res) => {
      if (res.error) ToastError(res.message);
      else setConfig(res.data);
    });
  }, []);

  useEffect(() => {
    if (!superAdmin) return;
    getBriskV2WalletSummary().then((res) => {
      if (!res.error && res.data && res.data.superAdmin) setAgencies(res.data.rows || []);
    });
  }, [superAdmin]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const query = useMemo(
    () => ({
      type: applied.type,
      clientId: applied.clientId,
      startDate: dayParam(applied.startDate),
      endDate: dayParam(applied.endDate),
      search: debouncedSearch,
    }),
    [applied, debouncedSearch]
  );

  const typeOptions = LEDGER_TYPE_FILTERS;
  const agencyList = agencyOptions(agencies);
  const toDate = (d) => (d && d.toDate ? d.toDate() : d);
  const apply = (patch = {}) => setApplied({ type, clientId: agency, startDate: toDate(startDate), endDate: toDate(endDate), ...patch });

  const filterData = [
    {
      id: 1,
      label: "Transaction Type",
      data: typeOptions,
      setState: (e) => {
        const option = typeOptions.find((el) => el._id === e);
        if (option) setType(option.value);
      },
      value: (typeOptions.find((opt) => opt.value === type) || typeOptions[0])._id,
      type: "input",
      variation: "single",
    },
    {
      id: 2,
      type: "noDefaultDate",
      label: "Transaction Date",
      defaultValue: [startDate, endDate],
      setDate: (dates) => {
        if (Array.isArray(dates) && dates.length >= 2 && dates[0] && dates[1]) {
          setStartDate(dates[0]);
          setEndDate(dates[1]);
          apply({ startDate: toDate(dates[0]), endDate: toDate(dates[1]) });
        } else {
          setStartDate(null);
          setEndDate(null);
          apply({ startDate: null, endDate: null });
        }
      },
    },
    ...(superAdmin
      ? [
          {
            id: 3,
            label: "Agency",
            data: agencyList,
            setState: (e) => {
              const option = agencyList.find((el) => el._id === e);
              if (option) setAgency(option.value);
            },
            value: (agencyList.find((opt) => opt.value === agency) || agencyList[0])._id,
            type: "input",
            variation: "single",
          },
        ]
      : []),
  ];

  const download = async () => {
    setExporting(true);
    const res = await getBriskV2WalletLedger({ ...query, export: "1" });
    setExporting(false);
    if (res.error) {
      ToastError(res.message);
      return;
    }
    const headers = superAdmin ? ["Agency", ...LEDGER_EXCEL_HEADERS] : LEDGER_EXCEL_HEADERS;
    await saveExcel(`Brisk Transaction Report ${moment().format("DD-MM-YYYY")}.xlsx`, headers, ledgerExcelRows(res.data.rows, { withAgency: superAdmin }));
    if (res.data.truncated) ToastError(`Only the newest ${res.data.rows.length} rows were downloaded. Please choose dates for the rest`);
  };

  const activeFiltersCount = (type ? 1 : 0) + (startDate && endDate ? 1 : 0) + (agency ? 1 : 0);
  const totals = (summary && summary.totals) || null;
  const wallet = (summary && summary.wallet) || null;

  return (
    <div style={{ margin: "1px" }}>
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="Master_Card_Tittle">
              Transaction Report
              {config && config.apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={8} className="d-flex" justifyContent={{ xs: "start", sm: "flex-end", md: "flex-end" }}>
            <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2} mt={{ xs: 2, sm: 2, md: 0 }}>
              <Button
                className="Master_Card_Filter_Button"
                sx={{ height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" }}
                startIcon={<FileDownloadOutlined />}
                disabled={exporting}
                onClick={download}
              >
                Download Excel
              </Button>
              <Button
                className="Master_Card_Filter_Button w-100"
                sx={{ width: "102px", height: "40px", gap: "8px" }}
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="error">
                    <FilterListIcon sx={{ marginRight: "1.5px" }} />
                  </Badge>
                }
                onClick={() => setShowFilter(!showFilter)}
              >
                Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {showFilter && (
          <Box sx={{ mb: 2, overflow: "visible", position: "relative", zIndex: 100, minHeight: "60px", width: "100%" }}>
            <FilterBox
              filterData={filterData}
              type={"single"}
              handleSaveAllFilter={() => apply()}
              handleClearAllState={() => {
                setType("");
                setAgency("");
                setStartDate(null);
                setEndDate(null);
                setApplied({ type: "", clientId: "", startDate: null, endDate: null });
              }}
              handleClearSingleFilter={(filterId) => {
                if (filterId === 1) setType("");
                if (filterId === 2) {
                  setStartDate(null);
                  setEndDate(null);
                }
                if (filterId === 3) setAgency("");
              }}
              horizontalLayout={true}
              hideHeader={true}
              hideInput={true}
              alwaysOpen={true}
              autoApply={true}
            />
          </Box>
        )}

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        <MismatchAlert wallet={wallet} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <MoneyCard label="Total Credit" value={totals ? totals.credit : ""} tone="#067647" note={totals ? `${totals.count} transactions` : ""} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MoneyCard label="Total Debit" value={totals ? totals.debit : ""} tone="#B42318" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MoneyCard
              label={superAdmin && !applied.clientId ? "Brisk Balance (all agencies)" : "Brisk Balance"}
              value={wallet ? wallet.balance : ""}
              note="Today, whatever the filters"
            />
          </Grid>
        </Grid>

        <Grid container alignItems={"center"} justifyContent={"flex-end"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3.1}>
            <Input
              className="w-100 Master_Card_Input"
              type="text"
              placeholder="Search certificate, policy, customer, chassis"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disableUnderline={true}
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton onClick={() => setDebouncedSearch(search)} aria-label="search">
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>

        <BriskLedgerTable query={query} showAgency={superAdmin} onLoaded={setSummary} />
      </Grid>
    </div>
  );
};

export default BriskTransactionReport;
